import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { legacyToActualMap, type Message, type MessageSource, type WhisperWord } from "../models";
import { ImgDown } from "./Images/ImgDown";
import { ImgUp } from "./Images/ImgUp";
import { ImgFileTxt } from "./Images/ImgFileTxt";
import { ImgWeb } from "./Images/ImgWeb";
import { ImgRetry } from "./Images/ImgRetry";
import { ImgTool } from "./Images/ImgTool.tsx";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { shouldShowModelLoadingProgress } from "./ChatMessageModelLoadingProgress.tsx";
import { ChatMessageSearchProgress } from "./ChatMessageSearchProgress.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import ContextMenu, { ContextMenuItem } from "./ContextMenu";

const TOOL_NAME_MAP: Record<string, string> = {
  "get_weather": "Get Weather",
  "get_date_time": "Get Date & Time",
  "fetch_page": "Fetch Page",
  "web_search": "Web Search",
  "telegram_read_channel": "Read Telegram",
  "read_youtube": "Read YouTube",
  "audio_to_text": "Transcribe Audio",
  "read_document": "Read Document",
  "generate_sdxl_image": "Generate SDXL Image",
  "generate_flux_image": "Generate Flux Image",
  "generate_z_image_image": "Generate Z-Image",
  "generate_image": "Generate Image",
  "update_image": "Update Image",
  "generate_video": "Generate Video",
  "ocr_image": "OCR Image",
  "generate_docx": "Generate DOCX",
  "generate_xlsx": "Generate XLSX",
  "generate_pptx": "Generate PPTX",
  "web_push": "Send Notification",
  "bitbucket_list_commits": "Bitbucket Commits",
  "bitbucket_list_commits_by_timeframe": "Bitbucket Commits",
  "bitbucket_read_file": "Bitbucket Read File",
  "bitbucket_list_pull_requests": "Bitbucket PRs",
  "bitbucket_get_branch_diff": "Bitbucket Diff",
  "bitbucket_read_commit": "Bitbucket Commit",
  "bitbucket_list_repos": "Bitbucket Repos",
  "bitbucket_me": "Bitbucket Me",
};

function truncateStr(s: string, max: number): string {
  return s.length > max ? s.substring(0, max) + '…' : s;
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    const display = u.hostname + (u.pathname !== '/' ? u.pathname : '');
    return truncateStr(display, 50);
  } catch {
    return truncateStr(url, 50);
  }
}

function getToolParamSummary(resolvedName: string, argsJson: string): string | null {
  try {
    const a = JSON.parse(argsJson);
    switch (resolvedName) {
      case 'web_search': return a.query ? `"${a.query}"` : null;
      case 'fetch_page': return a.url ? shortenUrl(a.url) : null;
      case 'telegram_read_channel': {
        const parts: string[] = [];
        if (a.channelName) parts.push(`@${a.channelName.replace(/^@/, '')}`);
        if (a.lastHours) parts.push(`${a.lastHours}h`);
        return parts.length ? parts.join(', ') : null;
      }
      case 'get_weather': return a.location || null;
      case 'get_date_time': return a.location || null;
      case 'read_youtube': return a.url ? shortenUrl(a.url) : null;
      case 'audio_to_text': return a.url ? shortenUrl(a.url) : null;
      case 'generate_docx':
      case 'generate_xlsx': return a.filename || null;
      case 'generate_pptx': return a.filename || null;
      case 'generate_flux_image':
      case 'generate_z_image_image':
        return a.prompt ? truncateStr(a.prompt, 60) : null;
      case 'generate_sdxl_image':
      case 'generate_image':
        return a.promptPositive ? truncateStr(a.promptPositive, 60) : null;
      case 'update_image': return a.operation || null;
      case 'generate_video': return a.prompt ? truncateStr(a.prompt, 60) : null;
      case 'ocr_image': return a.operation || null;
      case 'web_push': return a.title ? truncateStr(a.title, 40) : null;
      case 'bitbucket_list_commits':
      case 'bitbucket_list_commits_by_timeframe':
        return [a.repo, a.branch].filter(Boolean).join('/') || null;
      case 'bitbucket_read_file':
        return [a.repo, a.path].filter(Boolean).join(': ') || null;
      case 'bitbucket_list_pull_requests':
        return a.repo || null;
      case 'bitbucket_get_branch_diff':
        return [a.repo, a.source].filter(Boolean).join(': ') || null;
      case 'bitbucket_read_commit':
        return [a.repo, a.commitHash?.substring(0, 8)].filter(Boolean).join(': ') || null;
      case 'bitbucket_list_repos':
        return a.workspace || null;
      default: return null;
    }
  } catch {
    return null;
  }
}

function getUseBrowser(resolvedName: string, argsJson: string): boolean {
  if (resolvedName !== 'fetch_page') return false;
  try { return JSON.parse(argsJson).useBrowser === true; } catch { return false; }
}

function formatSize(size: number) {
  return size < 1024
    ? `${size} B`
    : size < 1024 * 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ns: number) {
  const sec = ns / 1_000_000_000;
  if (sec >= 60) return `${(sec / 60).toFixed(1)}m`;
  if (sec >= 1) return `${sec.toFixed(1)}s`;
  return `${(ns / 1_000_000).toFixed(0)}ms`;
}

type CompactThinkingLogProps = {
  message: Message;
  collapsedMessages?: Message[];
  isGenerating: boolean;
  hasModel?: boolean;
  generatingMessageId?: string | null;
  onViewToolResult: (message: Message) => void;
  onRegenerate?: (message: Message) => void;
  executingNode?: string | null;
  previewBlobUrl?: string | null;
  progress?: { current: number; max: number } | null;
  promptId?: string | null;
  onCancel?: () => void;
  showFullThinking?: boolean; // When true: show full log (default expanded); when false: show summary (default collapsed)
};

type StepEntry =
  | { type: 'thinking'; content: string; timeTaken: number | null; message: Message; isAnimating: boolean }
  | { type: 'tool_call'; name: string; resolvedName: string; displayName: string; arguments: string; message: Message; isAnimating: boolean }
  | { type: 'tool_result'; message: Message; size: number; isError: boolean; isAnimating: boolean };

// ─── Whisper word probability helpers ────────────────────────────────────────

function getWordConfidenceColor(p: number): string | undefined {
  if (p >= 0.92) return undefined;
  if (p >= 0.75) return 'rgba(60,200,60,0.22)';
  if (p >= 0.55) return 'rgba(200,190,0,0.28)';
  if (p >= 0.35) return 'rgba(230,120,0,0.28)';
  return 'rgba(220,40,40,0.28)';
}

function WhisperWordsView({ words }: { words: WhisperWord[] }) {
  return (
    <div className="compact-log-whisper-words">
      {words.map((w, i) => {
        const bg = getWordConfidenceColor(w.p);
        const tooltip = `${Math.round(w.p * 100)}% confidence${w.t0 != null && w.t1 != null ? ` · ${(w.t0 / 1000).toFixed(2)}s–${(w.t1 / 1000).toFixed(2)}s` : ''}`;
        return (
          <span
            key={i}
            className="whisper-word"
            style={bg ? { backgroundColor: bg } : undefined}
            title={tooltip}
          >
            {w.word}{' '}
          </span>
        );
      })}
    </div>
  );
}

@observer
export class CompactThinkingLog extends React.Component<CompactThinkingLogProps> {
  @observable private _isExpanded: boolean;
  @observable private _bodyVisible: boolean;
  private _collapseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: CompactThinkingLogProps) {
    super(props);
    this._isExpanded = props.showFullThinking ?? false;
    this._bodyVisible = this._isExpanded;
    makeObservable(this);
  }

  @action private _expand = () => {
    if (this._collapseTimer) { clearTimeout(this._collapseTimer); this._collapseTimer = null; }
    this._bodyVisible = true;
    // One rAF so the body renders at grid 0fr before the open transition fires
    requestAnimationFrame(action(() => { this._isExpanded = true; }));
  };

  @action private _collapse = () => {
    if (!this._isExpanded) return;
    this._isExpanded = false;
    if (this._collapseTimer) clearTimeout(this._collapseTimer);
    this._collapseTimer = setTimeout(action(() => {
      this._bodyVisible = false;
      this._collapseTimer = null;
    }), 300);
  };

  componentWillUnmount(): void {
    if (this._collapseTimer) clearTimeout(this._collapseTimer);
  }

  componentDidUpdate(prevProps: CompactThinkingLogProps): void {
    if (prevProps.showFullThinking !== this.props.showFullThinking) {
      if (this.props.showFullThinking) this._expand();
      else this._collapse();
    }
  }

  render() {
    const { message, collapsedMessages, isGenerating, hasModel = false, generatingMessageId, onViewToolResult, onRegenerate,
      executingNode, previewBlobUrl, progress, promptId, onCancel, showFullThinking } = this.props;
    const steps: StepEntry[] = [];

    const messages = collapsedMessages || [ message ];
    // Track tool messages that were already interleaved with a multi-call assistant
    const interleavedToolMsgs = new Set<Message>();

    for (const msg of messages) {
      const isMsgGenerating = isGenerating && msg.id === generatingMessageId;

      if (msg.role === 'assistant') {
        if (msg.thinking) {
          steps.push({
            type: 'thinking',
            content: msg.thinking,
            timeTaken: msg.thinkingTimeTaken ?? null,
            message: msg,
            isAnimating: isMsgGenerating,
          });
        }

        const hasTool = (msg.toolCalls && msg.toolCalls.length > 0) || !!msg.toolsFunctionName;
        if (hasTool && msg.content) {
          steps.push({
            type: 'thinking',
            content: msg.content,
            timeTaken: null,
            message: msg,
            isAnimating: false,
          });
        }

        const toolCalls = msg.toolCalls && msg.toolCalls.length > 0
          ? msg.toolCalls
          : msg.toolsFunctionName
            ? [{ id: 'legacy-0', name: msg.toolsFunctionName, arguments: msg.toolsFunctionArguments || '{}' }]
            : [];

        // For multi-call: collect all subsequent tool messages to interleave
        const toolResultMessages = toolCalls.length > 1
          ? messages.filter(m => m.role === 'tool')
          : null;

        for (let k = 0; k < toolCalls.length; k++) {
          const tc = toolCalls[k];
          const resolvedName = (legacyToActualMap as Record<string, string>)[tc.name] ?? tc.name;
          const displayName = TOOL_NAME_MAP[resolvedName] ?? tc.name;
          steps.push({
            type: 'tool_call',
            name: tc.name,
            resolvedName,
            displayName,
            arguments: tc.arguments || '{}',
            message: msg,
            isAnimating: isMsgGenerating,
          });

          // Interleave matching result immediately after its call
          if (toolResultMessages) {
            const resultMsg = toolResultMessages.find(r => r.toolCallId === tc.id) ?? toolResultMessages[k];
            if (resultMsg) {
              interleavedToolMsgs.add(resultMsg);
              steps.push({
                type: 'tool_result',
                message: resultMsg,
                size: resultMsg.contentLength ?? resultMsg.content?.length ?? 0,
                isError: !!resultMsg.errorMessage,
                isAnimating: isGenerating && resultMsg.id === generatingMessageId,
              });
            }
          }
        }
      }

      // Only add tool_result step for messages that are part of a collapsed sequence.
      // Standalone tool messages (collapsedMessages===undefined) are already displayed
      // by ChatMessageProgress, so we skip them here to avoid duplication.
      if (msg.role === 'tool' && !interleavedToolMsgs.has(msg) && collapsedMessages !== undefined) {
        steps.push({
          type: 'tool_result',
          message: msg,
          size: msg.contentLength ?? msg.content?.length ?? 0,
          isError: !!msg.errorMessage,
          isAnimating: isMsgGenerating,
        });
      }
    }

    const messagesIncluded = collapsedMessages || [message];
    const isThisMessageGenerating = isGenerating && messagesIncluded.some(m => m.id === generatingMessageId);
    // The actual message object that has promptEvalTotal/modelLoadTotal (the inner generating message)
    const generatingMessage = isThisMessageGenerating
      ? (messagesIncluded.find(m => m.id === generatingMessageId) ?? message)
      : message;
    const hasProgressNodes = isThisMessageGenerating && shouldShowModelLoadingProgress(generatingMessage);
    const showWorkingIndicator = isThisMessageGenerating && steps.length === 0 && !hasProgressNodes && !message.content && !message.thinking;

    if (steps.length === 0 && !showWorkingIndicator) return null;

    const hasToolCalls = steps.some(s => s.type === 'tool_call');
    const canExpand = hasToolCalls || steps.length > 1;

    const renderStepProps = { isGenerating, hasModel, onViewToolResult, onRegenerate, executingNode, previewBlobUrl, progress, promptId, onCancel };

    // Full log content (all steps + progress indicators)
    const renderFullContent = () => (
      <>
        {steps.map((step, i) => (
          <CompactLogStep key={i} step={step} {...renderStepProps} />
        ))}
        {showWorkingIndicator && (
          <div className="compact-log-step compact-log-working">
            <div className="compact-log-toggle">
              <CompactLogIcon type="thinking" />
              <ThinkingIndicator><span className="compact-log-label">Working on it</span></ThinkingIndicator>
            </div>
          </div>
        )}
      </>
    );

    if (showFullThinking) {
      return (
        <div className="compact-log">
          {this._bodyVisible && (
            <div className={`compact-log-body${this._isExpanded ? ' compact-log-body--open' : ''}`}>
              <div className="compact-log-body-inner">
                {renderFullContent()}
              </div>
            </div>
          )}
          <button className="compact-log-expand-btn compact-log-expand-btn--collapse" onClick={this._isExpanded ? this._collapse : this._expand}>
            {this._isExpanded
              ? <><ImgUp size={12} /><span>Collapse</span></>
              : <><ImgDown size={12} /><span>Show thinking</span></>
            }
          </button>
        </div>
      );
    } else {
      // OFF mode: default collapsed.
      // When generating and collapsed, show only the current live step as a compact indicator.
      // When not generating, nothing shows when collapsed — just the expand button.
      const lastToolCall = [...steps].reverse().find(s => s.type === 'tool_call');
      const liveIndicator = !this._isExpanded && isThisMessageGenerating
        ? (lastToolCall
            ? <CompactLogStep key="live" step={lastToolCall} {...renderStepProps} />
            : showWorkingIndicator
              ? (
                <div className="compact-log-step compact-log-working">
                  <div className="compact-log-toggle">
                    <CompactLogIcon type="thinking" />
                    <ThinkingIndicator><span className="compact-log-label">Working on it</span></ThinkingIndicator>
                  </div>
                </div>
              )
              : null)
        : null;

      if (!liveIndicator && !canExpand) return null;

      return (
        <div className="compact-log compact-log--summary">
          {liveIndicator}
          {this._bodyVisible && (
            <div className={`compact-log-body${this._isExpanded ? ' compact-log-body--open' : ''}`}>
              <div className="compact-log-body-inner">
                {renderFullContent()}
              </div>
            </div>
          )}
          {canExpand && (
            <button className="compact-log-expand-btn" onClick={this._isExpanded ? this._collapse : this._expand}>
              {this._isExpanded
                ? <><ImgUp size={12} /><span>Collapse</span></>
                : <><ImgDown size={12} /><span>Show thinking</span></>
              }
            </button>
          )}
        </div>
      );
    }
  }
}

type CompactLogStepProps = {
  step: StepEntry;
  isGenerating: boolean;
  hasModel: boolean;
  onViewToolResult: (message: Message) => void;
  onRegenerate?: (message: Message) => void;
  executingNode?: string | null;
  previewBlobUrl?: string | null;
  progress?: { current: number; max: number } | null;
  promptId?: string | null;
  onCancel?: () => void;
}

@observer
class CompactLogStep extends React.Component<CompactLogStepProps> {
  @observable private _open: boolean;
  @observable private _streamingOpen: boolean = true;
  @observable private _now: number = Date.now();
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _contentRef = React.createRef<HTMLDivElement>();
  private _streamingRef = React.createRef<HTMLDivElement>();
  private _prevContent: string = '';
  private _prevStreamingText: string = '';
  private _disableAutoScroll: boolean = false;
  private _disableAutoScrollTimeoutId: number | null = null;
  private _isProgrammaticScroll: boolean = false;
  private _streamingDisableAutoScroll: boolean = false;
  private _streamingDisableAutoScrollTimeoutId: number | null = null;
  private _streamingIsProgrammaticScroll: boolean = false;

  constructor(props: CompactLogStepProps) {
    super(props);
    this._open = props.step.type === 'thinking' && props.step.content.length <= 200;
    if (this._open && props.step.type === 'thinking') {
      this._prevContent = props.step.content;
    }
    makeObservable(this);
  }

  private _onContentScroll = (): void => {
    if (this._isProgrammaticScroll) return;
    this._disableAutoScroll = true;
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    this._disableAutoScrollTimeoutId = window.setTimeout(() => {
      this._disableAutoScroll = false;
      this._disableAutoScrollTimeoutId = null;
    }, 1000);
  };

  private _onStreamingScroll = (): void => {
    if (this._streamingIsProgrammaticScroll) return;
    this._streamingDisableAutoScroll = true;
    if (this._streamingDisableAutoScrollTimeoutId) clearTimeout(this._streamingDisableAutoScrollTimeoutId);
    this._streamingDisableAutoScrollTimeoutId = window.setTimeout(() => {
      this._streamingDisableAutoScroll = false;
      this._streamingDisableAutoScrollTimeoutId = null;
    }, 1000);
  };

  private _scrollStreamingToEnd(): void {
    const el = this._streamingRef.current;
    if (!el || !this._streamingOpen) return;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 120;
    if (isAtBottom && !this._streamingDisableAutoScroll) {
      this._streamingIsProgrammaticScroll = true;
      el.scrollTop = el.scrollHeight;
      this._streamingIsProgrammaticScroll = false;
    }
  }

  private _scrollToEndIfNeeded(): void {
    if (this.props.step.type !== 'thinking') return;
    
    const contentEl = this._contentRef.current;
    if (!contentEl || !this._open) return;
    
    const currentContent = this.props.step.content;
    const isGenerating = this.props.isGenerating;
    const contentUpdated = currentContent !== this._prevContent;
    
    if (contentUpdated && isGenerating) {
      const isAtBottom = contentEl.scrollHeight - contentEl.scrollTop <= contentEl.clientHeight + 120;
      if (isAtBottom && !this._disableAutoScroll) {
        this._isProgrammaticScroll = true;
        contentEl.scrollTop = contentEl.scrollHeight;
        this._isProgrammaticScroll = false;
      }
    }
  }

  public componentDidUpdate(prevProps: CompactLogStepProps): void {
    const currentStep = this.props.step;

    // Scroll to end when content updates during generation
    if (this._open && this.props.isGenerating && currentStep.type === 'thinking' && this._prevContent !== currentStep.content) {
      this._scrollToEndIfNeeded();
    }

    // Update prevContent after scroll attempt
    if (currentStep.type === 'thinking' && this._open) {
      this._prevContent = currentStep.content;
    }

    // Auto-scroll streaming text
    if (currentStep.type === 'tool_result' && this.props.isGenerating) {
      const streamingText = currentStep.message.toolProgress?.streamingText || '';
      if (streamingText !== this._prevStreamingText) {
        this._scrollStreamingToEnd();
        this._prevStreamingText = streamingText;
      }
    }

    // Sync interval when animating state changes
    const prevAnimating = prevProps.step.type === 'thinking' && prevProps.step.isAnimating;
    const nowAnimating = currentStep.type === 'thinking' && (currentStep as { isAnimating: boolean }).isAnimating;
    if (prevAnimating !== nowAnimating) this._syncInterval();
  }

  componentDidMount() {
    this._syncInterval();
  }

  public componentWillUnmount(): void {
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    if (this._streamingDisableAutoScrollTimeoutId) clearTimeout(this._streamingDisableAutoScrollTimeoutId);
    if (this._intervalId !== null) { clearInterval(this._intervalId); this._intervalId = null; }
  }

  private _syncInterval() {
    const isThinkingLive = this.props.step.type === 'thinking' && this.props.step.isAnimating;
    if (isThinkingLive) {
      if (this._intervalId === null) {
        this._intervalId = setInterval(action(() => { this._now = Date.now(); }), 100);
      }
    } else {
      if (this._intervalId !== null) { clearInterval(this._intervalId); this._intervalId = null; }
    }
  }

  @action.bound
  private _toggleOpen(): void {
    this._open = !this._open;
    if (this._open && this.props.step.type === 'thinking') {
      this._prevContent = this.props.step.content;
      this._disableAutoScroll = false;
    }
  }

  @action.bound
  private _toggleStreamingOpen(): void {
    this._streamingOpen = !this._streamingOpen;
    if (this._streamingOpen) {
      this._streamingDisableAutoScroll = false;
    }
  }

  render() {
    const { step, isGenerating, hasModel, onViewToolResult, onRegenerate } = this.props;

    if (step.type === 'thinking') {
      let timeLabel = '';
      if (step.timeTaken != null) {
        timeLabel = ` for ${(step.timeTaken / 1000).toFixed(1)}s`;
      } else if (step.isAnimating && step.message._thinkingStartMs !== undefined) {
        const liveSec = (this._now - step.message._thinkingStartMs) / 1000;
        timeLabel = ` for ${liveSec.toFixed(1)}s`;
      }
      return (
        <div className={`compact-log-step compact-log-thinking ${this._open ? 'open' : ''}`}>
          <button className="compact-log-toggle" onClick={this._toggleOpen}>
            <CompactLogIcon type="thinking" />
            <span className="compact-log-label">Thought{timeLabel}</span>
            {this._open ? <ImgUp /> : <ImgDown />}
          </button>
          <div className="compact-log-thinking-content" ref={this._contentRef} onScroll={this._onContentScroll}>
              {step.content.split('\n\n').map((paragraph, i) => (
                <p key={i} className="compact-log-thinking-paragraph">{paragraph}</p>
              ))}
            </div>
        </div>
      );
    }

    if (step.type === 'tool_call') {
      const hasArgs = step.arguments && step.arguments !== '{}';
      const paramSummary = getToolParamSummary(step.resolvedName, step.arguments);
      const useBrowser = getUseBrowser(step.resolvedName, step.arguments);
      const canRegen = !isGenerating && hasModel && !!onRegenerate && !step.isAnimating;
      let formattedArgs = step.arguments;
      if (hasArgs) {
        try { 
          formattedArgs = JSON.stringify(JSON.parse(step.arguments), null, 2); 
        } catch { /* keep as-is - LLM can generate broken JSON */ }
      }
      return (
        <div className={`compact-log-step compact-log-tool-call ${this._open ? 'open' : ''}`}>
          <div className="compact-log-row">
            <button className="compact-log-toggle" onClick={() => hasArgs && this._toggleOpen()}>
              <CompactLogIcon type="tool" />
              {step.isAnimating
                ? <ThinkingIndicator><span className="compact-log-label">Executing: {step.displayName}{paramSummary ? <span className="compact-log-param"> — {paramSummary}</span> : null}</span></ThinkingIndicator>
                : <>
                    <span className="compact-log-label">Executed: {step.displayName}{paramSummary ? <span className="compact-log-param"> — {paramSummary}</span> : null}</span>
                    {useBrowser && <ImgWeb size={13} />}
                  </>}
              {hasArgs && (this._open ? <ImgUp /> : <ImgDown />)}
            </button>
            {canRegen && (
              <button className="compact-log-regen-btn" title="Regenerate" onClick={(e) => { e.stopPropagation(); onRegenerate!(step.message); }}>
                <ImgRetry size={12} />
              </button>
            )}
          </div>
          {hasArgs && (
            <pre className="compact-log-args">{formattedArgs}</pre>
          )}
        </div>
      );
    }

    if (step.type === 'tool_result') {
      const msg = step.message;
      const hasContent = !!msg.content;
      const timeLabel = typeof msg.timeTaken === 'number' ? formatTime(msg.timeTaken) : null;
      const sources = msg.sources;
      const { executingNode, previewBlobUrl, progress, promptId, onCancel } = this.props;
      const hasSearchProgress = !!(msg.toolProgress?.searchResults?.length || msg.toolProgress?.sitesProcessing?.length);
      const statusText = msg.toolProgress?.status;
      const streamingText = msg.toolProgress?.streamingText;
      const wordTokens = msg.toolProgress?.wordTokens;
      const cancelId = msg.toolProgress?.operationId || promptId;
      const hasStreamingContent = !!(wordTokens?.length || streamingText);
      return (
        <div className={`compact-log-step compact-log-result ${step.isError ? 'compact-log-error' : ''}`}>
          {step.isAnimating && !hasContent && (
            <>
              {!hasStreamingContent && (
                <div className="compact-log-toggle">
                  <CompactLogIcon type="result" />
                  {executingNode
                    ? <ThinkingIndicator><span className="compact-log-label">Executing: {executingNode}</span></ThinkingIndicator>
                    : statusText && !hasSearchProgress
                      ? <ThinkingIndicator><span className="compact-log-label">{statusText}</span></ThinkingIndicator>
                      : <ThinkingIndicator><span className="compact-log-label">Receiving result</span></ThinkingIndicator>
                  }
                  {cancelId && onCancel && (
                    <button className="compact-log-cancel-btn" onClick={onCancel} title="Cancel">
                      <ImgCancel size={12} />
                    </button>
                  )}
                </div>
              )}
              {hasStreamingContent && (
                <div className={`compact-log-step compact-log-thinking compact-log-streaming ${this._streamingOpen ? 'open' : ''}`}>
                  <div className="compact-log-row">
                    <button className="compact-log-toggle" onClick={this._toggleStreamingOpen}>
                      <CompactLogIcon type="result" />
                      {executingNode
                        ? <ThinkingIndicator><span className="compact-log-label">Executing: {executingNode}</span></ThinkingIndicator>
                        : statusText && !hasSearchProgress
                          ? <ThinkingIndicator><span className="compact-log-label">{statusText}</span></ThinkingIndicator>
                          : <ThinkingIndicator><span className="compact-log-label">{wordTokens?.length ? 'Transcription preview' : 'Transcribing…'}</span></ThinkingIndicator>
                      }
                      {this._streamingOpen ? <ImgUp /> : <ImgDown />}
                    </button>
                    {cancelId && onCancel && (
                      <button className="compact-log-cancel-btn" onClick={onCancel} title="Cancel">
                        <ImgCancel size={12} />
                      </button>
                    )}
                  </div>
                  <div className="compact-log-thinking-content" ref={this._streamingRef} onScroll={this._onStreamingScroll}>
                    {wordTokens?.length
                      ? <WhisperWordsView words={wordTokens} />
                      : streamingText!.split(/\n/).filter(Boolean).map((line, i) => (
                          <p key={i} className="compact-log-thinking-paragraph">{line}</p>
                        ))
                    }
                  </div>
                </div>
              )}
              {hasSearchProgress && (
                <div className="compact-log-search-progress">
                  <ChatMessageSearchProgress message={msg} />
                </div>
              )}
              {previewBlobUrl && (
                <div className="compact-log-image-progress">
                  <img className="compact-log-preview-img" src={previewBlobUrl} alt="Preview" />
                  {progress && (
                    <div className="compact-log-progress-bar">
                      <div className="compact-log-progress-fill" style={{ width: `${Math.min(100, progress.current / progress.max * 100)}%` }}></div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {hasContent && (
            <div className="compact-log-row">
              <div className="compact-log-toggle compact-log-result-row" onClick={() => onViewToolResult(msg)}>
                <ImgFileTxt size={14} />
                <span className="compact-log-label compact-log-result-label">
                  Result{timeLabel ? ` (${timeLabel})` : ''} &mdash; {formatSize(step.size)}
                </span>
                {step.isError && <span className="compact-log-error-badge">error</span>}
              </div>
              {!isGenerating && hasModel && onRegenerate && !step.isAnimating && (
                <button className="compact-log-regen-btn" title="Regenerate" onClick={(e) => { e.stopPropagation(); onRegenerate(msg); }}>
                  <ImgRetry size={12} />
                </button>
              )}
            </div>
          )}
          {hasContent && msg.whisperWords && msg.whisperWords.length > 0 && (
            <div className={`compact-log-step compact-log-thinking compact-log-streaming compact-log-whisper-done ${this._streamingOpen ? 'open' : ''}`}>
              <button className="compact-log-toggle" onClick={this._toggleStreamingOpen}>
                <CompactLogIcon type="result" />
                <span className="compact-log-label">Transcription confidence</span>
                {this._streamingOpen ? <ImgUp /> : <ImgDown />}
              </button>
              <div className="compact-log-thinking-content">
                <WhisperWordsView words={msg.whisperWords} />
              </div>
            </div>
          )}
          {sources && sources.length > 0 && (
            <CompactLogSources sources={sources} />
          )}
          {step.isError && msg.errorMessage && (
            <div className="compact-log-error-text">{msg.errorMessage}</div>
          )}
        </div>
      );
    }

    return null;
  }
}

@observer
class CompactLogSources extends React.Component<{ sources: MessageSource[] }> {
  private _buttonRef = React.createRef<HTMLButtonElement>();
  private _maxVisible = 5;

  render() {
    const { sources } = this.props;
    const visibleSources = sources.slice(0, this._maxVisible);
    const extraCount = sources.length - this._maxVisible;

    return (
      <div className="compact-log-sources">
        <button ref={this._buttonRef} className="compact-log-sources-btn" title="View sources">
          <span className="compact-log-sources-favicons">
            {visibleSources.map((s, i) => (
              <span key={i} className="source-favicon-wrap">
                {s.favicon ? (
                  <img
                    src={s.favicon}
                    alt=""
                    className="source-favicon"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                      (img.nextElementSibling as HTMLElement | null)?.classList.remove('source-favicon-fallback--hidden');
                    }}
                  />
                ) : null}
                <span className={`source-favicon-fallback${s.favicon ? ' source-favicon-fallback--hidden' : ''}`}>
                  {(s.title || s.link).charAt(0).toUpperCase()}
                </span>
              </span>
            ))}
            {extraCount > 0 && <span className="sources-extra">+{extraCount}</span>}
          </span>
          <span className="compact-log-sources-label">{sources.length} source{sources.length !== 1 ? 's' : ''}</span>
        </button>
        <ContextMenu container={this._buttonRef} showOnClick>
          {() => (
            <>
              {sources.map((s, i) => (
                <ContextMenuItem
                  key={i}
                  onClick={() => window.open(s.link, '_blank', 'noopener,noreferrer')}
                  onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(s.link, '_blank', 'noopener,noreferrer'); } }}
                  title={
                    <span className="source-menu-item">
                      <span className="source-menu-favicon-wrap">
                        {s.favicon ? (
                          <img
                            src={s.favicon}
                            alt=""
                            className="source-menu-favicon"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              (img.nextElementSibling as HTMLElement | null)?.classList.remove('source-favicon-fallback--hidden');
                            }}
                          />
                        ) : null}
                        <span className={`source-favicon-fallback source-menu-favicon-fallback${s.favicon ? ' source-favicon-fallback--hidden' : ''}`}>
                          {(s.title || s.link).charAt(0).toUpperCase()}
                        </span>
                      </span>
                      <span className="source-menu-text">
                        <span className="source-menu-title">{s.title || s.link}</span>
                        <span className="source-menu-url">{s.link}</span>
                      </span>
                      <svg className="source-menu-external" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </span>
                  }
                />
              ))}
            </>
          )}
        </ContextMenu>
      </div>
    );
  }
}

const CompactLogIcon: React.FC<{ type: 'thinking' | 'tool' | 'result' }> = ({ type }) => {
  if (type === 'thinking') {
    return (
      <svg className="compact-log-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 6.5c0-1.1.9-2 2-2s2 .9 2 2c0 .7-.4 1.3-.9 1.6-.3.2-.6.5-.6.9v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="8" cy="11.5" r=".7" fill="currentColor"/>
      </svg>
    );
  }
  if (type === 'tool') {
    return (
      <ImgTool size={14} />
    );
  }
  return (
    <svg className="compact-log-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 8L7.5 9.5L10.5 6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
};

export default CompactThinkingLog;